import { SITE_URL, DEFAULT_OG_IMAGE } from './seoConfig';

// Tier/pricing facts sourced from CLAUDE.md's "Business Model" section -- keep in sync with that
// doc if pricing or tier features ever change, rather than treating this as a second source of truth.
export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'RosterRx',
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
};

export const SOFTWARE_APPLICATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'RosterRx',
  applicationCategory: 'SportsApplication',
  operatingSystem: 'Web',
  url: SITE_URL,
  description:
    'Fantasy football rankings aggregator and AI-powered live draft assistant. Aggregates ESPN, Yahoo, and FantasyPros rankings into a consensus view.',
  offers: [
    {
      '@type': 'Offer',
      name: 'Free',
      price: '0',
      priceCurrency: 'USD',
      description: 'Rankings aggregation from up to 3 sources, plus CSV import/export.',
    },
    {
      '@type': 'Offer',
      name: 'Pro',
      price: '5',
      priceCurrency: 'USD',
      description:
        'Live draft mode with AI pick suggestions, mock draft practice, unlimited ranking sources, multi-league support, and in-season AI tools.',
    },
  ],
};

/**
 * Builds Article + BreadcrumbList schema for a content page (R3.4). `path` is the route's own
 * path (e.g. '/glossary'); breadcrumb is always Home -> this page, matching the site's flat
 * (non-nested) content structure.
 *
 * `datePublished`/`dateModified` (R5.4) are optional, caller-supplied ISO date strings (e.g.
 * '2026-07-27') -- deliberately NOT defaulted to the current date here, since that would make
 * every page falsely claim same-day freshness on every deploy regardless of whether its content
 * actually changed. Each call site tracks its own small, manually-maintained date constant next
 * to its content, updated by hand only when the prose itself changes.
 */
export function buildArticleSchema({ path, title, description, datePublished, dateModified }) {
  const url = `${SITE_URL}${path}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url,
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    publisher: {
      '@type': 'Organization',
      name: 'RosterRx',
      logo: DEFAULT_OG_IMAGE,
    },
  };
}

/**
 * Builds AboutPage schema (R5.4) for the About page -- same shape/signature as
 * buildArticleSchema so both are called the same way from a page's <Helmet>.
 */
export function buildAboutPageSchema({ path, title, description }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: title,
    description,
    url: `${SITE_URL}${path}`,
    about: {
      '@type': 'Organization',
      name: 'RosterRx',
      logo: DEFAULT_OG_IMAGE,
    },
  };
}

/**
 * Builds ItemList schema for the rankings page (R5.4). Capped at ITEM_LIST_MAX_ITEMS so the
 * schema can never outgrow what the prerendered snapshot's own default (unfiltered, first-page)
 * view actually shows -- a schema block asserting hundreds of players a crawler's static HTML
 * doesn't itself render would be a structured-data/visible-content mismatch, not a benefit.
 * `items` should already be in the same order as the visible table (default sort).
 */
export const ITEM_LIST_MAX_ITEMS = 50;

export function buildItemListSchema({ items, path }) {
  const capped = items.slice(0, ITEM_LIST_MAX_ITEMS);
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: `${SITE_URL}${path}`,
    numberOfItems: capped.length,
    itemListElement: capped.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.playerName,
    })),
  };
}

/**
 * Safely serializes a schema object for embedding inside a <script type='application/ld+json'>
 * tag. `JSON.stringify` alone doesn't escape '<', so a value containing a literal '</script>'
 * (e.g. ItemList's player names, sourced from scraped third-party data -- not fully trusted input)
 * would prematurely close the script tag in the raw HTML the prerender step writes to disk,
 * letting whatever follows be parsed as real markup instead of JSON text -- a textbook
 * JSON-in-<script> XSS vector (OWASP: never embed untrusted data in a <script> context, JSON
 * included). Escaping '<' alone is sufficient and round-trips through JSON.parse identically,
 * since `<` decodes back to '<'. Every JSON-LD <script> tag in the app should use this
 * instead of raw JSON.stringify, even ones built entirely from this file's own hardcoded
 * constants today -- the safety belongs to the serialization boundary, not to today's data flow.
 */
export function toJsonLd(schema) {
  return JSON.stringify(schema).replace(/</g, '\\u003c');
}

export function buildBreadcrumbSchema({ path, title }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: `${SITE_URL}${path}`,
      },
    ],
  };
}
